import React, { memo, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { filtersSliceActions } from '../../../store/modules/filters/reducer';
import useDebouncedCallback from 'global/react/hooks/useDebounceCallback';
import InputAutoComplete from 'global/react-mui/atoms/InputAutoComplete';
import { suggestedInputSelector } from '../../../store/modules/filters/selectors';
import { useGetLocationSuggestionQuery } from '../../../store/modules/services/searchPartnersApi/searchPartnersApi';
import { makeStyles } from '@material-ui/core';
import { translationsSelector } from '../../../store/modules/translations/selectors';
import { MapInitialState, mapSliceActions } from '../../../store/modules/map/reducer';
import { ILocationSuggestion } from 'wolf/components/organisms/o-fhw-search/o-fhw-search.data-utils';
import { apiUrlsSelector } from '../../../store/modules/fhw/selectors';
import { partnerProfileDrawerIsOpenSelector } from '../../../store/modules/ui/selectors';

const getOptions = (data) => {
	return (
		data?.data.map((location) => {
			const singleSuggestion = `${location.city} (${location.postcode})`;
			return singleSuggestion;
		}) || []
	);
};

const LocationAutoComplete: React.FC<Record<string, unknown>> = () => {
	const dispatch = useDispatch();
	const classes = useStyles();
	const currentValue = useSelector(suggestedInputSelector);
	const apiUrls = useSelector(apiUrlsSelector);
	const { searchFilterPLZLabel, searchFilterPLZPlaceholder } = useSelector(translationsSelector);
	const shouldBeDisabled = useSelector(partnerProfileDrawerIsOpenSelector);
	const [inputValue, setInputValue] = useState('');
	const [locationSuggestions, setLocationSuggestions] = useState<ILocationSuggestion[]>([]);

	const { parsedOptions, options, isLoading } = useGetLocationSuggestionQuery(
		{ value: inputValue, endpoint: apiUrls?.getLocationSuggestions },
		{
			selectFromResult: ({ data, ...rest }) => ({ parsedOptions: getOptions(data), options: data?.data || [], ...rest }),
			skip: inputValue === '',
		},
	);

	const setInputToSuggest = (value: string) => {
		const currentOption = locationSuggestions.find((suggestion) => `${suggestion.city} (${suggestion.postcode})` === value);

		const mapCentre: MapInitialState['mapCentre'] = {
			lat: currentOption?.lat || -1,
			lng: currentOption?.lng || -1,
		};

		dispatch(
			filtersSliceActions.setSuggestedInput({
				text: value,
				...mapCentre,
				zip: currentOption?.postcode || '',
			}),
		);

		// pan map to postcode lat/lng
		if (currentOption) {
			dispatch(mapSliceActions.setMapCentre(mapCentre));
			dispatch(mapSliceActions.setMapZoom(14));
		}
	};

	const handleSetInputToSuggest = useDebouncedCallback(setInputToSuggest, 200);

	// This is just updating the input field on autocomplete, it will not trigger any new query
	const handleOnInputChange = (value: React.SetStateAction<string>) => setInputValue(value);

	useEffect(() => {
		if (options.length) setLocationSuggestions(options);
	}, [options]);

	return (
		<>
			<div className={classes.hiddenOnMobile}>
				<InputAutoComplete
					placeholder={searchFilterPLZPlaceholder}
					value={currentValue.text}
					inputValue={inputValue}
					handleOnChange={handleSetInputToSuggest}
					handleOnInputChange={handleOnInputChange}
					label={searchFilterPLZLabel}
					options={parsedOptions}
					formControlProps={{ width: '100%', maxWidth: '380px' }}
					disabled={shouldBeDisabled}
				/>
				{/* Support for loading if needed - in future use an icon in the input? */}
				{/* { isLoading && <span>Loading...</span> } */}
			</div>
			<div className={classes.hiddenOnDesktop}>
				<InputAutoComplete
					placeholder={searchFilterPLZLabel}
					value={currentValue.text}
					inputValue={inputValue}
					handleOnChange={handleSetInputToSuggest}
					handleOnInputChange={handleOnInputChange}
					formControlProps={{ minHeight: 42, style: { justifyContent: 'center', width: 'auto' } }}
					options={parsedOptions}
					disabled={shouldBeDisabled}
				/>
			</div>
		</>
	);
};

const useStyles = makeStyles((theme) => ({
	hiddenOnMobile: {
		display: 'none',
		[theme.breakpoints.up('lg')]: {
			display: 'block',
		},
	},
	hiddenOnDesktop: {
		display: 'block',
		[theme.breakpoints.up('lg')]: {
			display: 'none',
		},
	},
}));

export default memo(LocationAutoComplete);
